import { z } from 'zod';
/**
 * Zod schema for API payloads: parent field must match `value` for the child to show.
 */
export const parentConditionValueSchema = z.object({
    value: z.unknown(),
});
/**
 * Returns whether the parent's submitted value satisfies the stored condition.
 *
 * - CHECKBOX: direct equality (boolean).
 * - SELECT: direct equality (string).
 * - MULTISELECT: the condition stores an array of trigger values; the field is
 *   visible when the parent's current value (also an array) includes at least
 *   one of them.
 */
export function parentValueMatchesParentCondition(parentFieldValue, condition) {
    if (condition === null || condition === undefined) {
        return false;
    }
    if (typeof condition !== 'object' || !('value' in condition)) {
        return false;
    }
    const { value: expected } = condition;
    // MULTISELECT: expected is an array of trigger values, parent is an array of selected values
    if (Array.isArray(expected)) {
        if (!Array.isArray(parentFieldValue)) {
            return false;
        }
        return expected.some((v) => parentFieldValue.includes(v));
    }
    // CHECKBOX / SELECT: direct equality
    // Treat null/undefined parent values as false for boolean conditions
    const normalizedParent = expected === false && parentFieldValue == null ? false : parentFieldValue;
    return Object.is(normalizedParent, expected);
}
/**
 * Returns a short label for checkbox conditions in admin UIs.
 * @deprecated Use {@link parentConditionDisplayLabel} instead.
 */
export function parentConditionCheckboxDisplayLabel(condition) {
    return parentConditionDisplayLabel(condition);
}
/**
 * Returns a human-readable label describing a parent condition for admin UIs.
 *
 * - CHECKBOX (`{ value: true/false }`): "checked" / "unchecked"
 * - SELECT (`{ value: "option" }`): the option string
 * - MULTISELECT (`{ value: ["a", "b"] }`): comma-separated list of option strings
 */
export function parentConditionDisplayLabel(condition) {
    if (condition === null || condition === undefined) {
        return '';
    }
    if (condition.value === true) {
        return 'checked';
    }
    if (condition.value === false) {
        return 'unchecked';
    }
    if (typeof condition.value === 'string') {
        return `"${condition.value}"`;
    }
    if (Array.isArray(condition.value)) {
        return condition.value.map((v) => `"${v}"`).join(', ');
    }
    return '';
}
